import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts'
import { Button } from '@structural-climbing/ui'
import { type Discipline, gradesForDiscipline } from '@structural-climbing/validation'
import { useGymThemeContext } from '../../layouts/useGymTheme'
import { useGymGradeConfig } from '../../layouts/useGymGradeConfig'
import { trimToUsedGrades } from './usedGradeRange'

type Period = 'week' | 'month' | 'quarter'

interface GradeCount {
  grade: number
  count: number
}

interface ChartRow {
  grade: string
  current: number
  previous: number
}

interface TooltipPayloadEntry {
  dataKey: string
  value: number
  color: string
}

interface CustomTooltipProps {
  active?: boolean
  label?: string
  payload?: TooltipPayloadEntry[]
}

function CustomTooltip({ active, label, payload }: CustomTooltipProps) {
  const { t } = useTranslation()
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-lg border border-divider bg-content1 px-3 py-2 shadow-md">
      <p className="mb-1 text-xs font-semibold">{label}</p>
      {payload.map((entry) => (
        <p key={entry.dataKey} className="text-xs" style={{ color: entry.color }}>
          {entry.dataKey === 'current'
            ? t('routes.comparisonTooltipCurrent', { count: entry.value })
            : t('routes.comparisonTooltipPrevious', { count: entry.value })}
        </p>
      ))}
    </div>
  )
}

interface Props {
  apiUrl: string
  getAccessTokenSilently: () => Promise<string>
  discipline?: Discipline
}

export function RouteDistributionComparisonChart({
  apiUrl,
  getAccessTokenSilently,
  discipline = 'boulder',
}: Props) {
  const { t } = useTranslation()
  const { primaryColor } = useGymThemeContext()
  const gradeConfig = useGymGradeConfig()

  const [period, setPeriod] = useState<Period>('month')
  const [data, setData] = useState<ChartRow[]>([])
  const [loading, setLoading] = useState(true)

  const periodDays: Record<Period, number> = { week: 7, month: 30, quarter: 90 }

  const loadData = useCallback(
    async (p: Period) => {
      setLoading(true)
      try {
        const token = await getAccessTokenSilently()
        const now = new Date()
        const days = periodDays[p]
        const currentFrom = new Date(now.getTime() - days * 86400000)
        const previousFrom = new Date(now.getTime() - 2 * days * 86400000)

        const params = (from: Date, to: Date) =>
          `from=${from.toISOString()}&to=${to.toISOString()}&discipline=${discipline}`

        const [currentRes, previousRes] = await Promise.all([
          fetch(`${apiUrl}/climbing-routes/grade-distribution?${params(currentFrom, now)}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(
            `${apiUrl}/climbing-routes/grade-distribution?${params(previousFrom, currentFrom)}`,
            { headers: { Authorization: `Bearer ${token}` } },
          ),
        ])

        const currentCounts: GradeCount[] = currentRes.ok
          ? ((await currentRes.json()) as GradeCount[])
          : []
        const previousCounts: GradeCount[] = previousRes.ok
          ? ((await previousRes.json()) as GradeCount[])
          : []

        const currentMap = new Map(currentCounts.map((g) => [g.grade, g.count]))
        const previousMap = new Map(previousCounts.map((g) => [g.grade, g.count]))

        setData(
          trimToUsedGrades(
            gradesForDiscipline(gradeConfig, discipline).map((g) => ({
              grade: g.label,
              current: currentMap.get(g.rank) ?? 0,
              previous: previousMap.get(g.rank) ?? 0,
            })),
            (row) => row.current > 0 || row.previous > 0,
            discipline,
          ),
        )
      } finally {
        setLoading(false)
      }
    },
    [getAccessTokenSilently, apiUrl, gradeConfig, discipline],
  )

  useEffect(() => {
    void loadData(period)
  }, [period, loadData])

  const isEmpty = data.every((d) => d.current === 0 && d.previous === 0)
  const barColor = primaryColor ?? 'hsl(var(--heroui-primary))'
  const prevColor = 'hsl(var(--heroui-foreground) / 0.2)'

  const PERIOD_KEYS: Period[] = ['week', 'month', 'quarter']
  const PERIOD_LABELS: Record<Period, string> = {
    week: t('routes.comparisonPeriodWeek'),
    month: t('routes.comparisonPeriodMonth'),
    quarter: t('routes.comparisonPeriodQuarter'),
  }

  return (
    <div>
      <p className="text-base font-semibold">{t('routes.comparisonChartTitle')}</p>
      <p className="text-xs text-foreground/50">{t('routes.comparisonChartSubtitle')}</p>

      <div className="mt-2 mb-3 flex gap-1.5">
        {PERIOD_KEYS.map((p) => (
          <Button
            key={p}
            size="sm"
            radius="full"
            variant={period === p ? 'solid' : 'flat'}
            color={period === p ? 'primary' : 'default'}
            className="h-7 min-w-0 font-semibold"
            aria-pressed={period === p}
            onClick={() => setPeriod(p)}
          >
            {PERIOD_LABELS[p]}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="h-[160px] animate-pulse rounded-lg bg-content2" />
      ) : isEmpty ? (
        <div className="flex h-[160px] items-center justify-center text-xs text-foreground/40">
          {t('routes.comparisonEmpty')}
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
            <XAxis
              dataKey="grade"
              tick={{ fontSize: 12, fill: 'hsl(var(--heroui-foreground) / 0.5)' }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: 'currentColor', fillOpacity: 0.05 }}
            />
            <Legend
              formatter={(value: string) =>
                value === 'current'
                  ? t('routes.comparisonCurrentLabel')
                  : t('routes.comparisonPreviousLabel')
              }
              wrapperStyle={{ fontSize: 11 }}
            />
            <Bar dataKey="current" fill={barColor} radius={[3, 3, 0, 0]} maxBarSize={20} />
            <Bar dataKey="previous" fill={prevColor} radius={[3, 3, 0, 0]} maxBarSize={20} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  )
}
